import { FC, Fragment, useMemo, useState } from 'react';
import { Layer, Marker, Popup, Source } from 'react-map-gl';
import { useAtomValue } from 'jotai';
import { builderSelectedFormationAtom } from './atoms';
import { useMergeGeometries } from './use-merge-geometries';
import style from './unit-builder-modal.module.scss';
import tooltipStyle from './merge-end-tooltip.module.scss';

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

const lineFeatures = (
  lines: GeoJSON.Position[][]
): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: lines.map((coordinates) => ({
    type: 'Feature',
    geometry: { type: 'LineString', coordinates },
    properties: {},
  })),
});

/**
 * Merge Geometries on the builder map while the panel is open: the merging
 * laterals from other formations (the map otherwise shows only the selected
 * formation), the clickable lateral ends and the tangent preview — all from the
 * server's preview. Saved composites are drawn by the shared slot layer.
 *
 * @return {JSX.Element | null} The layers.
 */
export const MergeGeometriesLayer: FC = () => {
  const formation = useAtomValue(builderSelectedFormationAtom)?.formation;
  const merge = useMergeGeometries(formation ?? null);
  const { draft, landingOptions, bottomHoleOptions, previewComposite } = merge;
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  const otherFormationLaterals = useMemo(() => {
    const fmt = formation?.toUpperCase() ?? null;
    return lineFeatures(
      (draft?.preview?.legs ?? [])
        .filter((leg) => leg.formationName.toUpperCase() !== fmt)
        .map((leg) => leg.ends.map((e) => e.coordinates.slice(0, 2)))
    );
  }, [draft?.preview, formation]);

  const tangents = useMemo(() => {
    const geo = previewComposite?.tangentsGeojson;
    return geo ? lineFeatures(geo.coordinates) : EMPTY_FC;
  }, [previewComposite]);

  if (!draft) return null;

  const pickTarget = merge.landingKey ? 'bottomHole' : 'landing';
  const allowedBottom = new Set(bottomHoleOptions.map((o) => o.key));
  const pick = (key: string) => {
    if (key === merge.landingKey) merge.assignEnd('landing', null);
    else if (key === merge.bottomHoleKey) merge.assignEnd('bottomHole', null);
    else merge.assignEnd(pickTarget, key);
  };

  return (
    <>
      <Source
        id="merge-other-formation-laterals"
        type="geojson"
        data={otherFormationLaterals}
      >
        <Layer
          id="merge-other-formation-laterals-line"
          type="line"
          paint={{
            'line-color': '#08bdba',
            'line-width': 3,
            'line-dasharray': [2, 1.5],
          }}
        />
      </Source>
      <Source id="merge-preview-tangents" type="geojson" data={tangents}>
        <Layer
          id="merge-preview-tangents-line"
          type="line"
          paint={{
            'line-color': '#009d9a',
            'line-width': 3,
            'line-dasharray': [2, 1.5],
          }}
        />
      </Source>
      {landingOptions.map((end) => {
        const isLanding = end.key === merge.landingKey;
        const isBottom = end.key === merge.bottomHoleKey;
        const blocked =
          pickTarget === 'bottomHole' &&
          !isLanding &&
          !isBottom &&
          !allowedBottom.has(end.key);
        let tip = `${end.label} · set as ${
          pickTarget === 'landing' ? 'landing point' : 'bottom hole'
        }`;
        if (isLanding) tip = `Landing point · ${end.label}`;
        else if (isBottom) tip = `Bottom hole · ${end.label}`;
        else if (blocked) {
          tip =
            end.slotId === draft.landing?.slotId
              ? `${end.label} · same LTD as the landing point`
              : `${end.label} · not a possible bottom hole`;
        }
        // As the mock: the picked ends keep their tooltip open, the rest on hover.
        const showTip = isLanding || isBottom || hoverKey === end.key;
        return (
          <Fragment key={end.key}>
            <Marker
              longitude={end.coordinates[0]}
              latitude={end.coordinates[1]}
              anchor="center"
            >
              <span
                onMouseEnter={() => setHoverKey(end.key)}
                onMouseLeave={() => setHoverKey(null)}
              >
                <button
                  type="button"
                  className={[
                    style.mergeEnd,
                    isLanding ? style.mergeEndLanding : '',
                    isBottom ? style.mergeEndBottom : '',
                    blocked ? style.mergeEndBlocked : '',
                  ].join(' ')}
                  aria-label={tip}
                  disabled={blocked}
                  onClick={(event) => {
                    event.stopPropagation();
                    pick(end.key);
                  }}
                />
              </span>
            </Marker>
            {showTip && (
              <Popup
                longitude={end.coordinates[0]}
                latitude={end.coordinates[1]}
                anchor="bottom"
                offset={isLanding || isBottom ? 13 : 11}
                closeButton={false}
                closeOnClick={false}
                maxWidth="none"
                className={tooltipStyle.tooltip}
              >
                {tip}
              </Popup>
            )}
          </Fragment>
        );
      })}
    </>
  );
};
